import { useEffect, useRef, useState } from "react";
import { Flame, Zap } from "lucide-react";

/**
 * ComboMeter — visible x1.5 → x2 → x3 XP/gold multiplier for chained kills.
 * Backend increments run.combo_count when kills land within 3 turns of each
 * other and resets otherwise. This component renders a glowing badge only
 * when combo_count ≥ 2 (real bonus applies).
 *
 * Also flashes a big center-screen "COMBO x3!" burst on each new tier via
 * a bumpKey trick — parent bumps the key when combo_count increases.
 */
const TIER = {
  2: { mult: 1.5, color: "#e25822", glow: "rgba(226,88,34,0.55)",  label: "COMBO" },
  3: { mult: 2.0, color: "#d4af37", glow: "rgba(212,175,55,0.65)", label: "SLAUGHTER" },
  4: { mult: 3.0, color: "#c15fbc", glow: "rgba(193,95,188,0.75)", label: "APEX" },
};

export default function ComboMeter({ comboCount = 0 }) {
  const [flash, setFlash] = useState(null);
  const lastCombo = useRef(0);

  useEffect(() => {
    if (comboCount > lastCombo.current && comboCount >= 2) {
      const t = TIER[comboCount] || TIER[4];
      setFlash({ key: Date.now(), tier: comboCount, ...t });
      const to = setTimeout(() => setFlash(null), 1600);
      lastCombo.current = comboCount;
      return () => clearTimeout(to);
    }
    lastCombo.current = comboCount;
  }, [comboCount]);

  if (comboCount < 2 && !flash) return null;
  const shown = TIER[Math.min(comboCount, 4)] || null;

  return (
    <>
      {/* Persistent HUD badge in the sidebar */}
      {shown && (
        <div
          data-testid="combo-meter-badge"
          className="panel px-3 py-2 flex items-center justify-between gap-2 relative overflow-hidden"
          style={{ borderColor: shown.color, boxShadow: `0 0 20px ${shown.glow}` }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(80px 30px at 20% 50%, ${shown.glow}, transparent 70%)`,
              animation: "combo-pulse 1.6s ease-in-out infinite",
              opacity: 0.5,
            }}
          />
          <div className="relative flex items-center gap-2">
            <Flame size={14} style={{ color: shown.color }} />
            <span className="text-[10px] tracking-[0.3em]" style={{ color: shown.color }}>
              {shown.label}
            </span>
          </div>
          <div className="relative font-heading text-lg" style={{ color: "#f0e6d2" }}>
            x{shown.mult}
          </div>
        </div>
      )}

      {/* Center-screen flash on each new tier */}
      {flash && (
        <div
          key={flash.key}
          data-testid="combo-flash"
          className="fixed inset-0 z-[68] pointer-events-none flex items-center justify-center"
          style={{ animation: "combo-flash-in 1.6s ease-out both" }}
        >
          <div
            style={{
              transform: "scale(1)",
              animation: "combo-flash-scale 1.6s cubic-bezier(0.2,0.9,0.3,1.15) both",
              textShadow: `0 0 40px ${flash.glow}, 0 4px 20px rgba(0,0,0,0.9)`,
            }}
          >
            <div className="text-[11px] tracking-[0.5em] text-center" style={{ color: flash.color }}>
              — {flash.label} —
            </div>
            <div
              className="font-heading italic text-center"
              style={{
                fontSize: "clamp(4rem, 12vw, 8rem)",
                lineHeight: 1,
                color: "#f0e6d2",
                letterSpacing: "0.02em",
              }}
            >
              COMBO <span style={{ color: flash.color }}>x{flash.mult}</span>
            </div>
            <div className="flex items-center justify-center gap-2 mt-3 text-[10px] tracking-[0.4em]" style={{ color: flash.color }}>
              <Zap size={14} /> BONUS XP + GOLD <Zap size={14} />
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes combo-pulse {
          0%,100% { opacity: 0.4; }
          50%     { opacity: 0.75; }
        }
        @keyframes combo-flash-in {
          0%   { opacity: 0; }
          10%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes combo-flash-scale {
          0%   { transform: scale(0.60); opacity: 0; }
          15%  { transform: scale(1.20); opacity: 1; }
          40%  { transform: scale(1.00); }
          80%  { transform: scale(1.00); opacity: 1; }
          100% { transform: scale(1.10); opacity: 0; }
        }
      `}</style>
    </>
  );
}
